'use client';

import Link from 'next/link';
import { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';

/**
 * FaqSection — короткие ответы перед финальным CTA.
 * Тёмная тема, чтобы продолжать настроение Scene 10 stories-блока, не разбивая
 * визуальную цепочку светлым "конвертом" перед финалом.
 */

const QUESTIONS = [
  {
    q: 'Это бесплатно?',
    a: 'Да. Сохранение, разбор каталога и уведомления — всё бесплатно. Распознавание текста со скриншотов и подсказки «🤔 Чем заняться?» — до 5 раз в день бесплатно, а подписка снимает эти лимиты — тарифы выше.',
  },
  {
    q: 'А оно не само раскидывает?',
    a: 'Нет. Бот спрашивает только одно — куда сохранить. Ты выбираешь категорию кнопкой — и всё. Можно задать правило для сайта (вроде «Кинопоиск → посмотреть») — тогда оттуда ссылки сразу пойдут в нужную категорию. Каталог запоминает твои правила, а не пытается угадать.',
  },
  {
    q: 'Мои записи кто-то видит?',
    a: 'Нет. Каталог приватный — только ты видишь, что сохранил. Никакой ленты, никаких подписчиков, никакого «социального» слоя.',
  },
  {
    q: 'Можно объединить избранное из Telegram и VK?',
    a: 'Да. Бот в Telegram и VK работает с общим каталогом — если подключить оба, всё, что сохранил в одном, сразу появится в другом. Привязка через /link: короткий код из бота, который вставляешь во второй мессенджер. После этого избранное живёт в одном месте, и веб видит всё вместе.',
  },
  {
    q: 'А можно сохранять файлы?',
    a: 'Да. PDF, DOC, XLSX, картинки, голосовые — всё, что отправишь боту, прикрепится к записи. Файлы хранятся в самом Telegram или VK, бот держит только ссылку — поэтому лимит размера такой же, как у обычных файлов в мессенджере. На странице записи в вебе файл скачивается одной кнопкой.',
  },
  {
    q: 'Что если надоест — можно всё удалить?',
    a: 'Да, одной командой /clear в боте — и всё. Никаких условий, подтверждений «передумайте», попыток удержать. Удалилось — значит удалилось.',
  },
  {
    q: 'Нужно устанавливать приложение?',
    a: 'Нет. Можно пользоваться прямо в боте в Telegram или VK. Если хочется — в любой момент добавишь каталог на главный экран телефона как обычное приложение (инструкция есть в справке), но это опционально.',
  },
  {
    q: 'А чем это отличается от закладок или заметок?',
    a: 'Закладки и заметки нужны, чтобы сохранить. А здесь задача другая — помочь вернуться к сохранённому и разобрать. Каталог сам предлагает одну запись, а не показывает стопку из двухсот.',
  },
];

export default function FaqSection() {
  const reduced = useReducedMotion() ?? false;
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="scene-faq" className="relative w-full bg-[#0d1a0c] py-20 sm:py-24 px-5 sm:px-8 overflow-hidden">
      <BgOrbs variant="warm" />

      <div className="relative max-w-3xl mx-auto">
        {/* Заголовок */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-10 sm:mb-12"
        >
          <h2
            className="font-display font-bold text-white leading-[1] tracking-[-0.02em] mb-3"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
          >
            Вопросы и <span className="text-[#f5a85a]">ответы.</span>
          </h2>
          <p className="font-body text-white/70 text-base sm:text-lg">
            Коротко и ясно.
          </p>
        </motion.div>

        {/* Accordion */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-3xl border border-white/10 overflow-hidden divide-y divide-white/8 bg-white/[0.04] backdrop-blur-sm"
          style={{ boxShadow: '0 24px 48px -24px rgba(0,0,0,0.6)' }}
        >
          {QUESTIONS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full text-left px-6 py-5 hover:bg-white/[0.03] transition-colors cursor-pointer group"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="font-display font-semibold text-white text-base sm:text-lg leading-snug flex-1">
                      {item.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="shrink-0 w-7 h-7 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors"
                      aria-hidden
                    >
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 24 24">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </motion.span>
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="font-body text-white/75 text-base leading-relaxed px-6 pb-5 -mt-1">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>

        {/* Link to full FAQ */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mt-8"
        >
          <Link
            href="/faq"
            className="group inline-flex items-center gap-2 font-body text-white/70 hover:text-white text-sm font-medium transition-colors"
          >
            Полная справка и FAQ
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
